import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, 
    Grid, 
    Paper, 
    Typography, 
    TextField, 
    Button, 
    Card, 
    CardContent,
    IconButton,
    Chip,
    Divider,
    Collapse,
    FormControlLabel,
    Switch,
    Tooltip,
    LinearProgress,
    Slider
} from '@mui/material';
import {
    Search as SearchIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    DragIndicator as DragIndicatorIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Add as AddIcon,
    Save as SaveIcon,
    Clear as ClearIcon,
    Preview as PreviewIcon,
    Psychology as PsychologyIcon,
    School as SchoolIcon,
    Science as ScienceIcon,
    Help as HelpIcon
} from '@mui/icons-material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import OperatorDetailModal from './components/OperatorDetailModal';
import OperatorUploadModal from './components/OperatorUploadModal';
import AcademicGuidanceModal from './components/AcademicGuidanceModal';
import ExpressionBrowser from './components/ExpressionBrowser';
import PromptPreviewModal from './components/PromptPreviewModal';

const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

// Operator Card Component for Library Panel
const LibraryOperatorCard = ({ operator, onAddToExpression, onViewDetails }) => {
    const [{ isDragging }, drag] = useDrag({
        type: 'operator',
        item: { operator, fromLibrary: true },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const getOperatorTypeIcon = (type) => {
        switch(type) {
            case 'published_scale': return <SchoolIcon color="primary" />;
            case 'user_scale': return <PsychologyIcon color="secondary" />;
            default: return <ScienceIcon color="action" />;
        }
    };

    const getOperatorTypeColor = (type) => {
        switch(type) {
            case 'published_scale': return 'primary';
            case 'user_scale': return 'secondary';
            default: return 'default';
        }
    };

    return (
        <Card 
            ref={drag}
            sx={{ 
                mb: 2, 
                opacity: isDragging ? 0.5 : 1,
                cursor: 'grab',
                '&:hover': { elevation: 4 },
                transition: 'all 0.2s ease'
            }}
            elevation={2}
        >
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {getOperatorTypeIcon(operator.type)}
                            <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
                                {operator.name}
                            </Typography>
                            <Chip 
                                label={operator.version ? `v${operator.version}` : 'v1.0'} 
                                size="small" 
                                sx={{ ml: 1 }}
                            />
                        </Box>
                        
                        <Chip 
                            label={operator.type === 'published_scale' ? 'Published Scale' : 'User Scale'}
                            color={getOperatorTypeColor(operator.type)}
                            size="small"
                            sx={{ mb: 1 }}
                        />
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {operator.description || 'No description available'}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            <Chip label={`${operator.factors?.length || 0} factors`} size="small" variant="outlined" />
                            <Chip label={`${Object.keys(operator.items || {}).length} items`} size="small" variant="outlined" />
                            <Chip 
                                label={`${operator.response_model?.type || 'Unknown'} Response`} 
                                size="small" 
                                variant="outlined" 
                            />
                        </Box>
                        
                        {operator.authors && (
                            <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                Authors: {operator.authors.join(', ')}
                            </Typography>
                        )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                        <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => onViewDetails(operator)}>
                                <ViewIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Add to Identity">
                            <IconButton size="small" onClick={() => onAddToExpression(operator)}>
                                <AddIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

// Expression Operator Card Component for Builder Panel
const ExpressionOperatorCard = ({ operator, index, onUpdate, onRemove, onMoveOperator }) => {
    const [expanded, setExpanded] = useState(false);
    const [factorValues, setFactorValues] = useState({});

    useEffect(() => {
        // Initialize factor values from operator data
        const initialValues = {};
        operator.factors?.forEach((factor, idx) => {
            initialValues[idx] = factor.value !== undefined ? factor.value : operator.response_model?.default_value || 3;
        });
        setFactorValues(initialValues);
    }, [operator]);

    const handleFactorChange = (factorIndex, value) => {
        const newFactorValues = { ...factorValues, [factorIndex]: value };
        setFactorValues(newFactorValues);
        
        // Update the operator with new factor values
        const updatedFactors = operator.factors?.map((factor, idx) => ({
            ...factor,
            value: newFactorValues[idx] !== undefined ? newFactorValues[idx] : factor.value
        }));
        
        onUpdate(index, { ...operator, factors: updatedFactors });
    };

    const [{ isDragging }, drag] = useDrag({
        type: 'operator',
        item: { operator, fromExpression: true, originalIndex: index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [{ isOver }, drop] = useDrop({
        accept: 'operator',
        hover: (draggedItem) => {
            if (draggedItem.fromExpression && draggedItem.originalIndex !== index) {
                onMoveOperator(draggedItem.originalIndex, index);
                draggedItem.originalIndex = index;
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    });

    return (
        <Card 
            ref={(node) => drag(drop(node))}
            sx={{ 
                mb: 2, 
                opacity: isDragging ? 0.5 : 1,
                border: '2px solid',
                borderColor: isOver ? 'secondary.main' : 'primary.main',
                '&:hover': { borderColor: 'primary.dark' },
                backgroundColor: isOver ? 'action.hover' : 'background.paper'
            }}
        >
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Box 
                            sx={{ 
                                mr: 1, 
                                cursor: 'grab',
                                p: 0.5,
                                borderRadius: 1,
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                        >
                            <DragIndicatorIcon />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6">
                                {index + 1}. {operator.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                <Chip 
                                    label={`${operator.factors?.length || 0} factors`} 
                                    size="small" 
                                    variant="outlined"
                                />
                                {/* Show factor values when collapsed */}
                                {!expanded && operator.factors?.length > 0 && (
                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                        {operator.factors.slice(0, 3).map((factor, idx) => {
                                            const value = factorValues[idx] !== undefined ? factorValues[idx] : factor.value;
                                            const max = operator.response_model?.range?.[1] || 5;
                                            return (
                                                <Chip
                                                    key={idx}
                                                    label={`${factor.label}: ${value}/${max}`}
                                                    size="small"
                                                    color="primary"
                                                    variant="filled"
                                                />
                                            );
                                        })}
                                        {operator.factors.length > 3 && (
                                            <Chip
                                                label={`+${operator.factors.length - 3} more`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                    
                    <Box>
                        <IconButton onClick={() => setExpanded(!expanded)}>
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <IconButton onClick={() => onRemove(index)} color="error">
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                </Box>
                
                <Collapse in={expanded}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Factor Configuration
                    </Typography>
                    
                    {operator.factors?.map((factor, factorIndex) => {
                        const min = operator.response_model?.range?.[0] || 1;
                        const max = operator.response_model?.range?.[1] || 5;
                        const value = factorValues[factorIndex] !== undefined ? factorValues[factorIndex] : factor.value || operator.response_model?.default_value || 3;
                        
                        return (
                            <Box key={factorIndex} sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>{factor.label}</strong> (Score: {value})
                                </Typography>
                                
                                <Box 
                                    sx={{ px: 2 }}
                                    onClick={(e) => e.stopPropagation()} // Prevent drag when interacting with slider
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    <Slider
                                        value={value}
                                        min={min}
                                        max={max}
                                        step={0.1}
                                        onChange={(e, newValue) => handleFactorChange(factorIndex, newValue)}
                                        valueLabelDisplay="auto"
                                        sx={{
                                            '& .MuiSlider-thumb': {
                                                '&:hover, &.Mui-focusVisible': {
                                                    boxShadow: 'none',
                                                },
                                            },
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onTouchStart={(e) => e.stopPropagation()}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                        <Typography variant="caption">{min}</Typography>
                                        <Typography variant="caption">{max}</Typography>
                                    </Box>
                                </Box>
                                
                                <Typography variant="caption" color="text.secondary">
                                    {factor.items_included?.length || 0} items included
                                </Typography>
                            </Box>
                        );
                    })}
                </Collapse>
            </CardContent>
        </Card>
    );
};

// Main Identity Builder Component
const IdentityBuilder = () => {
    const [operators, setOperators] = useState([]);
    const [filteredOperators, setFilteredOperators] = useState([]);
    const [expression, setExpression] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [expressionName, setExpressionName] = useState('');
    const [includeAutobiographic, setIncludeAutobiographic] = useState(false);
    const [autobiographicMemory, setAutobiographicMemory] = useState('');
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [guidanceModalOpen, setGuidanceModalOpen] = useState(false);
    const [promptPreviewOpen, setPromptPreviewOpen] = useState(false);
    const [promptPreview, setPromptPreview] = useState('');
    const [expressionBrowserOpen, setExpressionBrowserOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const { getAccessTokenSilently, isAuthenticated } = useAuth0();

    // Fetch operators on component mount
    useEffect(() => {
        const fetchOperators = async () => {
            try {
                const token = await getAccessTokenSilently();
                const response = await axios.get(`${API_URL}/api/simulations/operators`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setOperators(response.data);
                setFilteredOperators(response.data);
            } catch (error) {
                console.error('Failed to fetch operators:', error);
            } finally {
                setLoading(false);
            }
        };

        if (isAuthenticated) {
            fetchOperators();
        }
    }, [getAccessTokenSilently, isAuthenticated]);

    // Filter operators based on search and type
    useEffect(() => {
        let filtered = operators;
        
        if (searchTerm) {
            filtered = filtered.filter(op => 
                op.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                op.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                op.authors?.some(author => author.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
        
        if (filterType !== 'all') {
            // Handle both 'user_scale' and variations in the database
            filtered = filtered.filter(op => {
                if (filterType === 'user_scale') {
                    return op.type === 'user_scale' || op.type === 'user' || !op.type;
                } else {
                    return op.type === filterType;
                }
            });
        }
        
        setFilteredOperators(filtered);
    }, [operators, searchTerm, filterType]);

    const generatePromptPreview = useCallback(() => {
        if (expression.length === 0) {
            setPromptPreview('No operators selected. Your identity will be defined when you add psychological scales to this expression.');
            return;
        }

        let preview = 'AGENT IDENTITY DEFINITION:\n\n';
        
        expression.forEach((operator, index) => {
            preview += `${index + 1}. ${operator.name}\n`;
            if (operator.factors) {
                operator.factors.forEach(factor => {
                    if (factor.value !== undefined) {
                        preview += `   - ${factor.label}: ${factor.value}/${operator.response_model?.range?.[1] || 5}\n`;
                    }
                });
            }
            preview += '\n';
        });

        if (includeAutobiographic && autobiographicMemory.trim()) {
            preview += 'AUTOBIOGRAPHIC MEMORY:\n';
            preview += autobiographicMemory.trim() + '\n\n';
        }

        preview += 'This identity profile will be converted to specific behavioral instructions for the AI agent.';
        
        setPromptPreview(preview);
    }, [expression, includeAutobiographic, autobiographicMemory]);

    // Generate prompt preview when expression changes
    useEffect(() => {
        generatePromptPreview();
    }, [expression, includeAutobiographic, autobiographicMemory, generatePromptPreview]);

    const handleViewDetails = (operator) => {
        setSelectedOperator(operator);
        setDetailModalOpen(true);
    };

    const handleOperatorUploaded = (newOperator) => {
        // Refresh the operators list
        setOperators(prev => [...prev, newOperator]);
        setUploadModalOpen(false);
    };

    const handleLoadExpression = (savedExpression) => {
        // The server stores the data nested under 'expression' field
        const expressionData = savedExpression.expression || savedExpression;
        
        // Load the saved expression data
        setExpressionName(expressionData.name || '');
        setExpression(expressionData.expression || []);
        
        // Handle autobiographic memory if it exists
        if (expressionData.autobiographicMemory) {
            setAutobiographicMemory(expressionData.autobiographicMemory);
            setIncludeAutobiographic(true);
        } else {
            setAutobiographicMemory('');
            setIncludeAutobiographic(false);
        }
    };

    const handleMoveOperator = (fromIndex, toIndex) => {
        setExpression(prev => {
            const newExp = [...prev];
            const [movedOperator] = newExp.splice(fromIndex, 1);
            newExp.splice(toIndex, 0, movedOperator);
            return newExp;
        });
    };

    const handleAddToExpression = (operator) => {
        // Create a copy with default factor values
        const operatorCopy = {
            ...operator,
            factors: operator.factors?.map(factor => ({
                ...factor,
                value: factor.value !== undefined ? factor.value : operator.response_model?.default_value || 3
            }))
        };
        
        setExpression(prev => [...prev, operatorCopy]);
    };

    const handleUpdateOperator = (index, updatedOperator) => {
        setExpression(prev => prev.map((op, i) => i === index ? updatedOperator : op));
    };

    const handleRemoveOperator = (index) => {
        setExpression(prev => prev.filter((_, i) => i !== index));
    };

    const handleSaveExpression = async () => {
        if (!expressionName.trim()) {
            alert('Please enter a name for your identity expression.');
            return;
        }

        setSaving(true);
        try {
            const token = await getAccessTokenSilently();
            const expressionData = {
                name: expressionName,
                expression: expression, // Changed from 'operators' to 'expression' to match database structure
                autobiographicMemory: includeAutobiographic ? autobiographicMemory : null,
                date_created: new Date().toISOString()
            };

            await axios.post(`${API_URL}/api/simulations/expression`, 
                { data: expressionData },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            alert('Identity expression saved successfully!');
            // Don't clear the expression after saving - keep it for further editing
        } catch (error) {
            console.error('Failed to save expression:', error);
            alert('Failed to save expression. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleClearExpression = () => {
        setExpression([]);
        setExpressionName('');
        setAutobiographicMemory('');
        setIncludeAutobiographic(false);
    };

    // Drop zone for the expression builder
    const [{ isOver }, drop] = useDrop({
        accept: 'operator',
        drop: (item) => {
            if (item.fromLibrary) {
                handleAddToExpression(item.operator);
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    });

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <LinearProgress />
                <Typography>Loading operators...</Typography>
            </Box>
        );
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <Box sx={{ p: 3, height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ flexShrink: 0, mb: 3 }}>
                    <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PsychologyIcon sx={{ mr: 2, fontSize: '2rem' }} />
                            Academic Identity Builder
                        </Box>
                        <Button
                            variant="outlined"
                            startIcon={<HelpIcon />}
                            onClick={() => setGuidanceModalOpen(true)}
                        >
                            Academic Guide
                        </Button>
                    </Typography>
                </Box>
                
                <Grid container spacing={3} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    {/* Left Panel - Operator Library */}
                    <Grid item xs={4} sx={{ height: '100%', minHeight: 0 }}>
                        <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                <SchoolIcon sx={{ mr: 1 }} />
                                Operator Library
                            </Typography>
                            
                            {/* Search and Filter Controls */}
                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    fullWidth
                                    placeholder="Search operators..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                    }}
                                    sx={{ mb: 1 }}
                                />
                                
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <Button
                                        variant={filterType === 'all' ? 'contained' : 'outlined'}
                                        size="small"
                                        onClick={() => setFilterType('all')}
                                    >
                                        All
                                    </Button>
                                    <Button
                                        variant={filterType === 'published_scale' ? 'contained' : 'outlined'}
                                        size="small"
                                        onClick={() => setFilterType('published_scale')}
                                    >
                                        Published
                                    </Button>
                                    <Button
                                        variant={filterType === 'user_scale' ? 'contained' : 'outlined'}
                                        size="small"
                                        onClick={() => setFilterType('user_scale')}
                                    >
                                        User
                                    </Button>
                                </Box>
                                
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => setUploadModalOpen(true)}
                                    sx={{ mb: 2 }}
                                    fullWidth
                                >
                                    Upload New Operator
                                </Button>
                            </Box>
                            
                            {/* Operators List */}
                            <Box sx={{ flex: 1, overflow: 'auto' }}>
                                {filteredOperators.length === 0 ? (
                                    <Typography color="text.secondary" align="center">
                                        No operators found matching your criteria.
                                    </Typography>
                                ) : (
                                    filteredOperators.map(operator => (
                                        <LibraryOperatorCard
                                            key={operator._id}
                                            operator={operator}
                                            onAddToExpression={handleAddToExpression}
                                            onViewDetails={handleViewDetails}
                                        />
                                    ))
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                    
                    {/* Right Panel - Identity Construction */}
                    <Grid item xs={8} sx={{ height: '100%', minHeight: 0 }}>
                        <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                                    <ScienceIcon sx={{ mr: 1 }} />
                                    Identity Construction
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ViewIcon />}
                                    onClick={() => setExpressionBrowserOpen(true)}
                                >
                                    Load Expression
                                </Button>
                            </Box>
                            
                            {/* Expression Name and Controls */}
                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Identity Expression Name"
                                    value={expressionName}
                                    onChange={(e) => setExpressionName(e.target.value)}
                                    placeholder="e.g., Extraverted Researcher Profile"
                                    sx={{ mb: 2 }}
                                />
                                
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <Button
                                        variant="contained"
                                        startIcon={<SaveIcon />}
                                        onClick={handleSaveExpression}
                                        disabled={saving || expression.length === 0}
                                    >
                                        {saving ? 'Saving...' : 'Save Identity'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ClearIcon />}
                                        onClick={handleClearExpression}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<PreviewIcon />}
                                        onClick={() => setPromptPreviewOpen(true)}
                                    >
                                        Preview Prompt
                                    </Button>
                                </Box>
                            </Box>
                            
                            {/* Expression Builder Drop Zone */}
                            <Box
                                ref={drop}
                                sx={{
                                    flex: 1,
                                    border: '2px dashed',
                                    borderColor: isOver ? 'primary.main' : 'grey.300',
                                    borderRadius: 2,
                                    p: 2,
                                    backgroundColor: isOver ? 'primary.light' : 'transparent',
                                    overflow: 'auto',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {expression.length === 0 ? (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column',
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        height: '100%',
                                        color: 'text.secondary'
                                    }}>
                                        <Typography variant="h6" sx={{ mb: 1 }}>
                                            Drop operators here to build your identity
                                        </Typography>
                                        <Typography variant="body2">
                                            Drag psychological scales from the library to create a comprehensive identity profile
                                        </Typography>
                                    </Box>
                                ) : (
                                    expression.map((operator, index) => (                                            <ExpressionOperatorCard
                                                key={`${operator._id}-${index}`}
                                                operator={operator}
                                                index={index}
                                                onUpdate={handleUpdateOperator}
                                                onRemove={handleRemoveOperator}
                                                onMoveOperator={handleMoveOperator}
                                            />
                                    ))
                                )}
                            </Box>
                            
                            {/* Autobiographic Memory Section */}
                            <Box sx={{ mt: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={includeAutobiographic}
                                            onChange={(e) => setIncludeAutobiographic(e.target.checked)}
                                        />
                                    }
                                    label="Include Autobiographic Memory"
                                />
                                
                                <Collapse in={includeAutobiographic}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Personal Background Description"
                                        value={autobiographicMemory}
                                        onChange={(e) => setAutobiographicMemory(e.target.value)}
                                        placeholder="Describe the agent's personal history, experiences, and background that will influence their behavior..."
                                        sx={{ mt: 1 }}
                                    />
                                </Collapse>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
                
                {/* Operator Detail Modal */}
                <OperatorDetailModal
                    operator={selectedOperator}
                    open={detailModalOpen}
                    onClose={() => {
                        setDetailModalOpen(false);
                        setSelectedOperator(null);
                    }}
                    onAddToExpression={handleAddToExpression}
                />
                
                {/* Operator Upload Modal */}
                <OperatorUploadModal
                    open={uploadModalOpen}
                    onClose={() => setUploadModalOpen(false)}
                    onOperatorUploaded={handleOperatorUploaded}
                />
                
                {/* Academic Guidance Modal */}
                <AcademicGuidanceModal
                    open={guidanceModalOpen}
                    onClose={() => setGuidanceModalOpen(false)}
                />
                
                {/* Prompt Preview Modal */}
                <PromptPreviewModal
                    open={promptPreviewOpen}
                    onClose={() => setPromptPreviewOpen(false)}
                    promptContent={promptPreview}
                    expressionName={expressionName}
                />
                
                {/* Expression Browser Modal */}
                <ExpressionBrowser 
                    open={expressionBrowserOpen}
                    onClose={() => setExpressionBrowserOpen(false)}
                    onLoadExpression={(expr) => {
                        handleLoadExpression(expr);
                        setExpressionBrowserOpen(false);
                    }}
                    currentExpression={expression}
                    isModal={true}
                />
            </Box>
        </DndProvider>
    );
};

export default IdentityBuilder;
